import { useRef, useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useApprovedTestimonials } from '../hooks/useApprovedTestimonials'
import { TestimonialCard } from './TestimonialCard'

export function TestimonialsCarousel() {
  const { testimonials, loading } = useApprovedTestimonials()
  const isPausedRef = useRef(false)
  const [activeIndex, setActiveIndex] = useState(0)

  // Auto-advance every 4 seconds unless paused
  useEffect(() => {
    if (loading || testimonials.length <= 1) return
    const interval = setInterval(() => {
      if (!isPausedRef.current) {
        setActiveIndex((prev) => (prev + 1) % testimonials.length)
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [loading, testimonials.length])

  // Reset index when testimonials change
  useEffect(() => {
    setActiveIndex(0)
  }, [testimonials.length])

  if (!loading && testimonials.length === 0) return null

  return (
    <section className="bg-muted py-24 text-foreground">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-12 text-center">
          <p className="mb-3 text-sm font-semibold uppercase tracking-widest text-brand-orange">
            Client Reviews
          </p>
          <h2 className="text-3xl font-bold tracking-tight">What Our Clients Say</h2>
        </div>

        {/* Carousel */}
        <div
          className="relative mx-auto w-full max-w-[90vw] sm:max-w-2xl md:max-w-3xl lg:max-w-4xl"
          onMouseEnter={() => { isPausedRef.current = true }}
          onMouseLeave={() => { isPausedRef.current = false }}
          onTouchStart={() => { isPausedRef.current = true }}
          onTouchEnd={() => { setTimeout(() => { isPausedRef.current = false }, 2000) }}
        >
          {/* Card slot — CSS grid overlap so container auto-sizes to tallest card */}
          <div className="grid">
            {loading
              ? (
                <div className="flex min-h-[320px] items-center justify-center">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-orange border-t-transparent" />
                </div>
              )
              : testimonials.map((testimonial, index) => (
                <div
                  key={testimonial.id}
                  style={{ gridArea: '1 / 1' }}
                  aria-hidden={index !== activeIndex}
                  className={cn(
                    'transition-opacity duration-700',
                    index === activeIndex ? 'opacity-100' : 'pointer-events-none opacity-0'
                  )}
                >
                  <TestimonialCard testimonial={testimonial} />
                </div>
              ))}
          </div>

          {/* Dot indicators */}
          {!loading && testimonials.length > 1 && (
            <div className="mt-8 flex items-center justify-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  type="button"
                  aria-label={`Go to testimonial ${index + 1}`}
                  aria-current={index === activeIndex ? 'true' : undefined}
                  onClick={() => {
                    setActiveIndex(index)
                    isPausedRef.current = true
                    setTimeout(() => { isPausedRef.current = false }, 5000)
                  }}
                  className={cn(
                    'rounded-full transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-muted',
                    index === activeIndex
                      ? 'h-2.5 w-6 bg-brand-orange'
                      : 'h-2.5 w-2.5 bg-foreground/20 hover:bg-foreground/40'
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
